import { FC, useEffect } from 'react';
import styled from '@emotion/styled';
import { useSetAtom } from 'jotai';
import ButtonBase from 'src/components/Button';
import ModalLayout, { MobileModalType, ModalContent, ModalFooter, ModalHeader, ModalHeaderDescriptionRow, ModalHeaderTitle } from 'src/components/ModalLayout';
import { closeModalAtom } from 'src/stores/modal/atoms';
import { setFeatureSettingsAtom } from 'src/stores/user/atom';
import { FeatureSettingPopup } from 'src/stores/user/types';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { ModalType } from 'src/types/Modal';
import { pushEvent } from 'src/utils/event-gateway';
import { setTargetBlankToLinks } from 'src/utils/html';
const Wrap = styled.div`
  max-width: 500px;
`;
const Desc = styled(ModalHeaderDescriptionRow)`
  color: ${({
  theme
}) => theme.color.content9};
  a {
    color: ${({
  theme
}) => theme.color.content9};
  }
`;
const Button = styled(ButtonBase)`
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 95px;
  width: auto;
`;
const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row-reverse;
  justify-content: flex-start;
  gap: 8px;

  ${({
  theme
}) => theme.screenSize.mobile} {
    flex-direction: column;
    gap: 16px;
  }
`;
interface Props {
  popup: FeatureSettingPopup;
  onClose: () => void;
  onDisplay: () => void;
}
const FeatureSettingsConsentsModal: FC<Props> = ({
  popup,
  onClose,
  onDisplay
}) => {
  const setFeatureSettings = useSetAtom(setFeatureSettingsAtom);
  const closeModal = useSetAtom(closeModalAtom);
  useEffect(() => {
    if (popup.type === 'LIQUIDITY_SHARING') {
      pushEvent({
        eventName: EVENT_NAME.MIRROR__IMPRESSION_LIQUIDITY_POPUP,
        eventType: EVENT_TYPE.MIRROR,
        eventParams: {
          tab: 'mirror',
          page: 'main',
          target: 'liquidity_sharing_popup'
        }
      });
    } else {
      pushEvent({
        eventName: EVENT_NAME.SIGNUP__IMPRESSION_FEATURE_CONSENT_POPUP,
        eventType: EVENT_TYPE.SIGN_UP,
        eventParams: {
          page: 'main',
          action_category: 'impression',
          target: 'consent_popup',
          tab: 'mirror',
          consent_category: popup.type
        }
      });
    }
    onDisplay();
  }, [popup, onDisplay]);
  const handleAccept = () => {
    setFeatureSettings([{
      type: popup.type,
      value: true
    }]);
    if (popup.type === 'LIQUIDITY_SHARING') {
      pushEvent({
        eventName: EVENT_NAME.MIRROR__CLICK_LIQUIDITY_POPUP,
        eventType: EVENT_TYPE.MIRROR,
        eventParams: {
          tab: 'mirror',
          page: 'main',
          target: 'liquidity_sharing_popup',
          result: 'accept'
        }
      });
    } else {
      pushEvent({
        eventName: EVENT_NAME.SIGNUP__CLICK_FEATURE_CONSENT_POPUP,
        eventType: EVENT_TYPE.SIGN_UP,
        eventParams: {
          action_category: 'click',
          page: 'main',
          target: 'button',
          tab: 'mirror',
          result: 'accept'
        }
      });
    }
    closeModal(ModalType.FEATURE_SETTINGS_CONSENTS);
    onClose();
  };
  const handleDisagree = () => {
    if (!popup) return;
    setFeatureSettings([{
      type: popup.type,
      value: false
    }]);
    if (popup.type === 'LIQUIDITY_SHARING') {
      pushEvent({
        eventName: EVENT_NAME.MIRROR__CLICK_LIQUIDITY_POPUP,
        eventType: EVENT_TYPE.MIRROR,
        eventParams: {
          action_category: 'click',
          tab: 'mirror',
          page: 'main',
          target: 'liquidity_sharing_popup',
          result: 'reject'
        }
      });
    } else {
      pushEvent({
        eventName: EVENT_NAME.SIGNUP__CLICK_FEATURE_CONSENT_POPUP,
        eventType: EVENT_TYPE.SIGN_UP,
        eventParams: {
          page: 'main',
          action_category: 'click',
          target: 'button',
          tab: 'mirror',
          result: 'reject'
        }
      });
    }
    closeModal(ModalType.FEATURE_SETTINGS_CONSENTS);
    onClose();
  };
  return popup ? <ModalLayout isDim
  // TODO: 추후, dim 동작도 feature settings popup API에서 내려주도록 개선하면 좋을 듯
  // Liquidity Sharing 팝업은 dim으로 닫을 수 있도록 함
  // dim으로 닫는 경우, disagree 처리하지 않고 default 상태를 유지함 (=별도의 onClose prop을 설정하지 않음)
  closeWhenClickOutside={popup.type === 'LIQUIDITY_SHARING' ? true : false} mobileType={MobileModalType.BOTTOM_SHEET} data-testid='Feature-Settings-Consent-Modal' data-sentry-element="ModalLayout" data-sentry-component="FeatureSettingsConsentsModal" data-sentry-source-file="FeatureSettingsConsentsModal.tsx">
      <Wrap data-sentry-element="Wrap" data-sentry-source-file="FeatureSettingsConsentsModal.tsx">
        <ModalHeader data-sentry-element="ModalHeader" data-sentry-source-file="FeatureSettingsConsentsModal.tsx">
          <ModalHeaderTitle mobileType={MobileModalType.BOTTOM_SHEET} data-sentry-element="ModalHeaderTitle" data-sentry-source-file="FeatureSettingsConsentsModal.tsx">
            {popup.title}
          </ModalHeaderTitle>
        </ModalHeader>
        <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="FeatureSettingsConsentsModal.tsx">
          <Desc dangerouslySetInnerHTML={{
          __html: setTargetBlankToLinks(popup.content)
        }} data-sentry-element="Desc" data-sentry-source-file="FeatureSettingsConsentsModal.tsx" />
        </ModalContent>
      </Wrap>
      <ModalFooter data-sentry-element="ModalFooter" data-sentry-source-file="FeatureSettingsConsentsModal.tsx">
        <ButtonWrapper data-sentry-element="ButtonWrapper" data-sentry-source-file="FeatureSettingsConsentsModal.tsx">
          <Button data-testid='Confirm-Button' onClick={handleAccept} id='CONSENT_AGREE' data-sentry-element="Button" data-sentry-source-file="FeatureSettingsConsentsModal.tsx">
            {popup.agreeButtonText}
          </Button>
          {popup.disagreeButtonText && <Button id='CONSENTS_DISAGREE' onClick={handleDisagree} color='tertiary'>
              {popup.disagreeButtonText}
            </Button>}
        </ButtonWrapper>
      </ModalFooter>
    </ModalLayout> : null;
};
export default FeatureSettingsConsentsModal;